import Dialog from 'Components/Molecules/Dialog/Dialog';
import Button from 'Components/Atoms/Button/Button';
import OutlineAutomation from 'Components/Atoms/OutlineIcons/OutlineAutomation';
import OutlineContactSupport from 'Components/Atoms/OutlineIcons/OutlineContactSupport';
import usePlansAndPricingDialog from 'Hooks/usePlansAndPricingDialog';
import { Trans, useTranslation } from 'react-i18next';
import useProjectSelected from 'Hooks/useProjectSelected';
import { useUser } from 'Components/Organisms/UserProvider/useUser';
import useProjects from 'Hooks/useProjects';
import IconWarning from 'Components/Atoms/IconWarning/IconWarning';
import ProjectBlockedDialogPlanCard from './ProjectBlockedDialogPlanCard';
import { PLANS } from 'Utils/constants';
import basicImage from 'assets/bt-free.png';
import personalImage from 'assets/bt-personal.png';
import freelanceImage from 'assets/bt-freelance.png';
import businessImage from 'assets/bt-business.png';
import customImage from 'assets/bt-custom.png';
import Menu, { MenuItem, MenuList } from 'Components/Molecules/Menu';
import DeleteAccountDialog from 'Components/Organisms/DeleteAccountDialog/DeleteAccountDialog';
import { useState } from 'react';
import useContextualHelp from 'Hooks/useContextualHelp';

const formatLimit = (limit) => {
    return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    })
        .format(limit)
        .replace(/\s/g, '');
};

const LIMITS = {
    starter: 400_000,
    advanced: 1_000_000,
};

const plans = {
    [PLANS.PERSONAL]: {
        title: 'Personal',
        limit: LIMITS.starter,
        image: personalImage,
    },
    [PLANS.FREELANCE_1]: {
        title: 'Freelance',
        limit: LIMITS.starter,
        image: freelanceImage,
    },
    [PLANS.FREELANCE_2]: {
        title: 'Freelance',
        limit: LIMITS.starter,
        image: freelanceImage,
    },
    [PLANS.BUSINESS_1]: {
        title: 'Business Starter',
        limit: LIMITS.starter,
        image: businessImage,
    },
    [PLANS.BUSINESS_2]: {
        title: 'Business Starter',
        limit: LIMITS.starter,
        image: businessImage,
    },
    [PLANS.BUSINESS_3]: {
        title: 'Business Advanced',
        limit: LIMITS.advanced,
        image: businessImage,
        className: 'from-[#000000] to-[#666666]',
    },
    [PLANS.CUSTOM]: {
        title: 'Premium',
        limit: LIMITS.advanced,
        image: customImage,
        className: 'from-[#29d377] to-[#5068e8]',
    },
};

const FREE_PLAN = {
    title: '-',
    limit: LIMITS.starter,
    image: basicImage,
};

export default function ProjectBlockedDialog() {
    const { t } = useTranslation('common');
    const { openPlansAndPricing } = usePlansAndPricingDialog();
    const { isLoading: isProjectLoading, project } = useProjectSelected();
    const { projects, isLoading: isProjectsLoading } = useProjects();
    const { user, logout } = useUser();
    const [openDeleteAccountDialog, setOpenDeleteAccountDialog] = useState(false);
    const { openChatSupport } = useContextualHelp();

    const handleContactSupport = () => {
        openChatSupport();
    };

    const hasMultipleProjects = !isProjectsLoading && projects.length > 1;
    const isTrialExpired = !isProjectLoading && !project.subscription;
    const isClientUser = !!user && !user.is_support;
    const showBlockDialog = project.is_premium_blocked && !isTrialExpired && isClientUser && !hasMultipleProjects;

    const { subscription } = project;

    const { title, image, limit, className } = plans[subscription?.plan] || FREE_PLAN;

    if (openDeleteAccountDialog) {
        return <DeleteAccountDialog open={openDeleteAccountDialog} onClose={() => setOpenDeleteAccountDialog(false)} />;
    }

    return (
        <Dialog open={showBlockDialog} className="md:max-w-3xl max-w-lg" keepSupportChat>
            <div className="relative">
                <div className="md:grid grid-cols-[320px_1fr] gap-8 sm:p-4 md:p-2">
                    <div className="bg-dark-blue/10 rounded-xl justify-center items-center hidden md:flex">
                        <ProjectBlockedDialogPlanCard title={title} limit={limit} image={image} className={className} />
                    </div>
                    <div>
                        <div className="pt-4 pb-9 text-dark-blue">
                            <div className="flex justify-center md:justify-start mb-4">
                                <IconWarning size={48} />
                            </div>
                            <div className="text-xl sm:text-2xl font-semibold mb-3 text-pretty">
                                {t('project-blocked-dialog-title')}
                            </div>
                            <div className="sm:text-lg text-pretty mb-4">
                                <Trans t={t} i18nKey="project-blocked-dialog-description-1" shouldUnescape>
                                    {{
                                        planName: title,
                                        limit: formatLimit(limit),
                                    }}
                                </Trans>
                            </div>
                            <div className="sm:text-lg text-pretty">{t('project-blocked-dialog-description-2')}</div>
                        </div>

                        <div className="grid sm:flex gap-2 pb-4 sm:pb-6">
                            <Button className="w-full" variant="primary" onClick={() => openPlansAndPricing()}>
                                <OutlineAutomation />
                                {t('view-plans')}
                            </Button>
                            <Button className="w-full" onClick={handleContactSupport}>
                                <OutlineContactSupport />
                                {t('contact-support')}
                            </Button>
                        </div>
                    </div>
                </div>

                <Menu
                    placement="bottom-start"
                    icon="gear"
                    iconSize={20}
                    iconColor="rgba(32,32,32,.5)"
                    className="absolute top-0 right-0"
                    zIndex="var(--z-index-popover)"
                >
                    <MenuList>
                        <MenuItem onClick={() => setOpenDeleteAccountDialog(true)}>{t('delete-account')}</MenuItem>
                        <MenuItem onClick={() => logout()}>{t('logout')}</MenuItem>
                    </MenuList>
                </Menu>
            </div>
        </Dialog>
    );
}
